import React, { lazy, Suspense } from "react";
import Bugsnag from "@bugsnag/js";
import Root from "root";

import {
  createBrowserRouter,
  Navigate,
  useLocation,
  useParams,
  useRouteError,
} from "react-router-dom";
const HomePage = lazy(() =>
  import("./pages/home").then((m) => ({ default: m.HomePage }))
);

// Lazy-loaded components - Tier 3 (secondary routes, previously eager)
const LibraryPage = lazy(() =>
  import("./pages/library").then((m) => ({ default: m.LibraryPage }))
);
const PostsScreen = lazy(() => import("components/Posts/PostsScreen"));
const InvoicesPage = lazy(() =>
  import("./pages/profile-settings/billing/invoices-page").then((m) => ({
    default: m.InvoicesPage,
  }))
);

// Lazy-loaded components - Tier 1 (largest/least frequently used)
const IdeaPlannerApp = lazy(() =>
  import("@idea-planner/app").then((m) => ({ default: m.App }))
);
const InboxApp = lazy(() =>
  import("@inbox/app").then((m) => ({ default: m.InboxApp }))
);
const InboxV2App = lazy(() =>
  import("./@inbox-v2/index").then((m) => ({ default: m.InboxV2App }))
);
const InboxV2ReplyTemplatesPage = lazy(() =>
  import("./@inbox-v2/index").then((m) => ({
    default: m.InboxV2ReplyTemplatesPage,
  }))
);
const InboxV2TagsPage = lazy(() =>
  import("./@inbox-v2/index").then((m) => ({ default: m.InboxV2TagsPage }))
);
const WorkshopApp = lazy(() =>
  import("@workshop/app").then((m) => ({ default: m.WorkshopApp }))
);
const InsightsApp = lazy(() =>
  import("@insights/index").then((m) => ({ default: m.InsightsApp }))
);
const AdminApp = lazy(() =>
  import("@admin/index").then((m) => ({ default: m.AdminApp }))
);
const CalendarLegacyRoute = lazy(() =>
  import("./pages/calendar-uplift/calendar-legacy-route").then((m) => ({
    default: m.CalendarLegacyRoute,
  }))
);
const CalendarUpliftRoute = lazy(() =>
  import("./pages/calendar-uplift/calendar-uplift-route").then((m) => ({
    default: m.CalendarUpliftRoute,
  }))
);
const ConversationViewPage = lazy(() =>
  import("@inbox/pages/conversation-page").then((m) => ({
    default: m.ConversationViewPage,
  }))
);

// Lazy-loaded components - Tier 2 (medium size)
const ProfileSettings = lazy(
  () => import("./pages/profile-settings/profile-settings")
);
const UsersPage = lazy(() =>
  import("./pages/users").then((m) => ({ default: m.UsersPage }))
);
const AccountsPage = lazy(() =>
  import("./pages/accounts/page").then((m) => ({ default: m.AccountsPage }))
);
const CesdkPage = lazy(() =>
  import("pages/cesdk").then((m) => ({ default: m.CesdkPage }))
);
const BrandKitsPage = lazy(() =>
  import("features/brand-kits").then((m) => ({ default: m.BrandKitsPage }))
);
const StyleGuidePage = lazy(() =>
  import("./pages/style-guide").then((m) => ({ default: m.StyleGuidePage }))
);
const NotFoundPage = lazy(() =>
  import("./pages/not-found").then((m) => ({ default: m.NotFoundPage }))
);
const ApprovalsPage = lazy(() =>
  import("./pages/approvals").then((m) => ({ default: m.ApprovalsPage }))
);
const ApprovalsBoardPage = lazy(() =>
  import("./pages/approvals/board").then((m) => ({
    default: m.ApprovalsBoardPage,
  }))
);

import HomeIcon from "./pages/component/menu/icons/home-03.svg";
import LibIcon from "./pages/component/menu/icons/library-v2.svg";
import TemplatesIcon from "./pages/component/menu/icons/templates.svg";
import PlannerIcon from "./pages/component/menu/icons/planner.svg";
import CalendarIcon from "./pages/component/menu/icons/calendar.svg";
import BulkUploadIcon from "./pages/component/menu/icons/bulk-upload.svg";
import UpcomingIcon from "./pages/component/menu/icons/upcoming.svg";
import DraftsIcon from "./pages/component/menu/icons/drafts.svg";
import ArchiveIcon from "./pages/component/menu/icons/archive.svg";
import InsightsIcon from "./pages/component/menu/icons/insights.svg";
import SocialAccountsIcon from "./pages/component/menu/icons/social-accounts.svg";
import UsersIcon from "./pages/component/menu/icons/users.svg";
import SettingsIcon from "./pages/component/menu/icons/settings.svg";
import LabelsIcon from "./pages/component/menu/icons/labels-icon.svg";
import IdeaPlannerIcon from "./pages/component/menu/icons/workshop.svg";
import ExternalPreviewIcon from "./pages/component/menu/icons/external-preview.svg";
import { PAGES } from "./constants/Pages";
import { formBaseTheme } from "ui/ecosystems/forms/theme";
import { ThemeProvider } from "styled-components";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";
const PostTemplatesPage = lazy(() =>
  import("features/post-templates").then((m) => ({
    default: m.PostTemplatesPage,
  }))
);
const PlannerPage = lazy(() =>
  import("./pages/planner/planner-page").then((m) => ({
    default: m.PlannerPage,
  }))
);
import { BulkUploadRedirect } from "./pages/bulk-upload-redirect";
import { toError } from "./utils/to-error";

// Loading fallback component for lazy-loaded routes
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" />
  </div>
);

// Error fallback for lazy-loaded chunks that fail to load
class LazyErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Lazy component loading error:", error, errorInfo);
  }

  retry = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-screen gap-4">
          <i className="fa-solid fa-triangle-exclamation text-4xl text-red-500"></i>
          <h2 className="text-xl font-semibold text-neutral-900">
            Failed to load page
          </h2>
          <p className="text-sm text-neutral-600">
            There was an error loading this page. Please try again.
          </p>
          <button
            onClick={this.retry}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 active:bg-blue-700 transition-colors"
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Wrapper component for lazy-loaded routes with error boundary
// eslint-disable-next-line react/prop-types
const LazyRoute = ({ children }) => (
  <LazyErrorBoundary>
    <Suspense fallback={<LoadingFallback />}>{children}</Suspense>
  </LazyErrorBoundary>
);

// Keeps existing links to `/dashboard/approvals` working after making the
// Approvals Board the default approvals surface.
const ApprovalsIndexRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/dashboard/approvals/board${search}`} replace />;
};

// Redirect component that preserves query params and wildcard sub-paths

const RedirectWithParams = ({ to }) => {
  const { search } = useLocation();
  const { "*": splat } = useParams();
  const path = splat ? `${to}/${splat}` : to;
  return <Navigate to={`${path}${search}`} replace />;
};

const DashboardWrapper = ({ element, ...props }) => {
  return (
    <QueryParamProvider adapter={ReactRouter6Adapter}>
      <ThemeProvider theme={formBaseTheme}>
        {React.cloneElement(element, { ...props })}
      </ThemeProvider>
    </QueryParamProvider>
  );
};

// NOTE: (Andrey): ncyBreadcrumb - angular router apendix, need avoid in future

// Top-level error element for react-router. Catches unhandled errors in route
// loaders, actions, and render paths that escape component-level boundaries.
const RouteErrorFallback = () => {
  const error = useRouteError();

  if (Bugsnag.isStarted?.()) {
    Bugsnag.notify?.(toError(error));
  }
  console.error("[RouteErrorFallback]", error);

  return (
    <div className="flex flex-col items-center justify-center h-screen gap-4 px-4">
      <i className="fa-solid fa-triangle-exclamation text-4xl text-red-500"></i>
      <h2 className="text-xl font-semibold text-neutral-900">
        Something went wrong
      </h2>
      <p className="text-sm text-neutral-600 text-center max-w-md">
        An unexpected error occurred. Please try reloading the page. If the
        problem persists, contact support.
      </p>
      <button
        onClick={() => window.location.reload()}
        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 active:bg-blue-700 transition-colors"
      >
        Reload Page
      </button>
    </div>
  );
};

const routes = [
  {
    path: "/",
    element: <DashboardWrapper element={<Root />} />,
    errorElement: <RouteErrorFallback />,
    children: [
      {
        path: "dashboard",
        element: (
          <LazyRoute>
            <HomePage />
          </LazyRoute>
        ),
        ncyBreadcrumb: {
          label: "Home",
          icon: <HomeIcon />,
        },
        params: { title: "Home" },
      },
      {
        path: "dashboard/onboarding/:onboardingStage",
        element: (
          <LazyRoute>
            <HomePage />
          </LazyRoute>
        ),
        ncyBreadcrumb: {
          label: "Home",
          icon: <HomeIcon />,
        },
        params: { title: "Home" },
      },
      {
        path: "dashboard/inbox",
        element: (
          <LazyRoute>
            <InboxApp />
          </LazyRoute>
        ),
        ncyBreadcrumb: {
          label: "Inbox",
          customLinkInTitle: "report_inbox_bug",
        },
        params: { title: "Inbox" },
        children: [
          {
            path: "conversation/:conversationId",
            element: (
              <LazyRoute>
                <ConversationViewPage />
              </LazyRoute>
            ),
          },
        ],
      },
      {
        path: "dashboard/inbox-v2",
        element: (
          <LazyRoute>
            <InboxV2App />
          </LazyRoute>
        ),
        ncyBreadcrumb: {
          label: "Inbox",
        },
        params: { title: "Inbox" },
      },
      {
        path: "dashboard/inbox-v2/board/:boardId",
        element: (
          <LazyRoute>
            <InboxV2App />
          </LazyRoute>
        ),
        ncyBreadcrumb: {
          label: "Inbox",
        },
        params: { title: "Inbox" },
      },
      {
        path: "dashboard/inbox-v2/temporary-board",
        element: (
          <LazyRoute>
            <InboxV2App />
          </LazyRoute>
        ),
        ncyBreadcrumb: {
          label: "Inbox",
        },
        params: { title: "Inbox" },
      },
      {
        path: "dashboard/inbox-v2/reply-templates",
        element: (
          <LazyRoute>
            <InboxV2ReplyTemplatesPage />
          </LazyRoute>
        ),
        ncyBreadcrumb: {
          label: "Reply Templates",
        },
        params: { title: "Reply Templates" },
      },
      {
        path: "dashboard/inbox-v2/tags",
        element: (
          <LazyRoute>
            <InboxV2TagsPage />
          </LazyRoute>
        ),
        ncyBreadcrumb: {
          label: "Tags",
        },
        params: { title: "Tags" },
      },
      {
        path: "dashboard/workshop/labels",
        element: (
          <LazyRoute>
            <WorkshopApp />
          </LazyRoute>
        ),
        params: { title: "Workshop" },
        ncyBreadcrumb: {
          label: "Labels",
          icon: <LabelsIcon />,
        },
      },
      {
        path: "dashboard/idea-planner",
        element: (
          <LazyRoute>
            <IdeaPlannerApp key={"singleton-idea-planner-app"} />
          </LazyRoute>
        ),
        params: { title: "Ideas" },
        ncyBreadcrumb: {
          label: "Ideas",
          icon: <IdeaPlannerIcon />,
        },
      },
      {
        path: "dashboard/library",
        element: (
          <LazyRoute>
            <LibraryPage />
          </LazyRoute>
        ),
        params: { title: "Library" },
        ncyBreadcrumb: {
          label: "Library",
          icon: <LibIcon />,
        },
      },
      {
        path: "dashboard/calendar",
        element: (
          <LazyRoute>
            <CalendarLegacyRoute />
          </LazyRoute>
        ),
        params: { title: "Calendar" },
        ncyBreadcrumb: {
          label: "Calendar",
          icon: <CalendarIcon />,
        },
      },
      {
        path: "dashboard/calendar-new",
        headerVariant: "calendar-uplift",
        element: (
          <LazyRoute>
            <CalendarUpliftRoute />
          </LazyRoute>
        ),
        params: { title: "Calendar" },
        ncyBreadcrumb: {
          label: "Calendar",
          icon: <CalendarIcon />,
        },
      },
      {
        path: "dashboard/accounts",
        element: (
          <LazyRoute>
            <AccountsPage />
          </LazyRoute>
        ),
        params: { title: "Manage Accounts" },
        ncyBreadcrumb: {
          label: "Manage Accounts",
          icon: <SocialAccountsIcon />,
        },
      },
      {
        path: "dashboard/groups",
        element: (
          <LazyRoute>
            <AccountsPage />
          </LazyRoute>
        ),
        params: { title: "Manage Groups" },
        ncyBreadcrumb: {
          label: "Manage Accounts",
          icon: <SocialAccountsIcon />,
        },
      },
      {
        path: "dashboard/users",
        element: (
          <LazyRoute>
            <UsersPage />
          </LazyRoute>
        ),
        params: { title: "Users" },
        ncyBreadcrumb: {
          label: "Users",
          icon: <UsersIcon />,
        },
      },
      {
        path: "dashboard/approvals",
        element: <ApprovalsIndexRedirect />,
      },
      {
        path: "dashboard/approvals/board",
        element: (
          <LazyRoute>
            <ApprovalsBoardPage />
          </LazyRoute>
        ),
        params: { title: "Approvals" },
        ncyBreadcrumb: {
          label: "Approvals",
          subLabel: "Board",
          icon: <ExternalPreviewIcon />,
        },
      },
      {
        path: "dashboard/approvals/links-themes",
        element: (
          <LazyRoute>
            <ApprovalsPage />
          </LazyRoute>
        ),
        params: { title: "Approvals" },
        ncyBreadcrumb: {
          label: "Approvals",
          icon: <ExternalPreviewIcon />,
        },
      },
      {
        path: "dashboard/schedules/dashboard/active",
        element: (
          <LazyRoute>
            <PostsScreen page={PAGES.UPCOMING} />
          </LazyRoute>
        ),
        params: { title: "Upcoming Posts" },
        ncyBreadcrumb: {
          label: "Upcoming Posts",
          icon: <UpcomingIcon />,
        },
      },
      {
        path: "dashboard/schedules/dashboard/inactive",
        element: (
          <LazyRoute>
            <PostsScreen page={PAGES.ARCHIVE} />
          </LazyRoute>
        ),
        params: { title: "Archive" },
        ncyBreadcrumb: {
          label: "Archive",
          icon: <ArchiveIcon />,
        },
      },
      {
        path: "dashboard/schedules/dashboard/drafts",
        element: (
          <LazyRoute>
            <PostsScreen page={PAGES.DRAFT} />
          </LazyRoute>
        ),
        params: { title: "Drafts" },
        ncyBreadcrumb: {
          label: "Drafts",
          icon: <DraftsIcon />,
        },
      },
      {
        path: "dashboard/contact",
        element: <Navigate to="/dashboard/" replace />,
      },
      {
        path: "dashboard/profile",
        element: (
          <LazyRoute>
            <ProfileSettings />
          </LazyRoute>
        ),
        params: { title: "Profile Settings" },
        ncyBreadcrumb: {
          label: "Profile",
          icon: <SettingsIcon />,
        },
      },
      {
        path: "dashboard/subscription",
        element: (
          <LazyRoute>
            <ProfileSettings />
          </LazyRoute>
        ),
        params: { title: "Subscription" },
        ncyBreadcrumb: {
          label: "Subscription",
          icon: <SettingsIcon />,
        },
      },
      {
        path: "dashboard/collaborate",
        element: (
          <LazyRoute>
            <ProfileSettings />
          </LazyRoute>
        ),
        params: { title: "Collaborate settings" },
        ncyBreadcrumb: {
          label: "Collaborate Settings",
          icon: <SettingsIcon />,
        },
      },
      {
        path: "dashboard/profile/settings/meta-ads",
        element: (
          <LazyRoute>
            <ProfileSettings />
          </LazyRoute>
        ),
        params: { title: "Meta Ads Settings" },
        ncyBreadcrumb: {
          label: "Meta Ads Settings",
          icon: <SettingsIcon />,
        },
      },
      {
        path: "dashboard/profile/settings/mcp",
        element: (
          <LazyRoute>
            <ProfileSettings />
          </LazyRoute>
        ),
        params: { title: "Sked MCP" },
        ncyBreadcrumb: {
          label: "Sked MCP",
          icon: <SettingsIcon />,
        },
      },
      {
        path: "dashboard/change-password",
        element: (
          <LazyRoute>
            <ProfileSettings />
          </LazyRoute>
        ),
        params: { title: "Password" },
        ncyBreadcrumb: {
          label: "Password",
          icon: <SettingsIcon />,
        },
      },
      {
        path: "dashboard/invoices",
        element: (
          <LazyRoute>
            <InvoicesPage />
          </LazyRoute>
        ),
        params: { title: "Invoices" },
        ncyBreadcrumb: {
          label: "Invoices",
          icon: <SettingsIcon />,
        },
      },
      {
        path: "dashboard/referral",
        element: (
          <LazyRoute>
            <ProfileSettings />
          </LazyRoute>
        ),
        params: { title: "Referral" },
        ncyBreadcrumb: {
          label: "Referral",
          icon: <SettingsIcon />,
        },
      },
      {
        path: "dashboard/profile/2faRegistration",
        element: (
          <LazyRoute>
            <ProfileSettings />
          </LazyRoute>
        ),
        params: { title: "Two-Factor Authentication" },
        ncyBreadcrumb: {
          label: "Two-Factor Authentication",
          icon: <SettingsIcon />,
        },
      },
      // Canonical deep-link for every profile-settings section.
      // `:section` is a slug (e.g. "sessions", "billing", "team-collaboration")
      // resolved by profile-settings.tsx. Legacy per-section routes above
      // still work (bookmarks, external links) and take precedence when they
      // match more specifically.
      {
        path: "dashboard/profile/:section",
        element: (
          <LazyRoute>
            <ProfileSettings />
          </LazyRoute>
        ),
        params: { title: "Profile Settings" },
        ncyBreadcrumb: {
          label: "Profile",
          icon: <SettingsIcon />,
        },
      },
      // Deep-link into a section's sub-view. Only the Integrations hub interprets
      // `:subPage` today (a provider key → connection detail, SKE-3972); other
      // sections ignore it and render the same as `/dashboard/profile/:section`.
      {
        path: "dashboard/profile/:section/:subPage",
        element: (
          <LazyRoute>
            <ProfileSettings />
          </LazyRoute>
        ),
        params: { title: "Profile Settings" },
        ncyBreadcrumb: {
          label: "Profile",
          icon: <SettingsIcon />,
        },
      },
      {
        path: "dashboard/template-manager",
        element: (
          <LazyRoute>
            <PostTemplatesPage />
          </LazyRoute>
        ),
        params: { title: "Template Manager" },
        ncyBreadcrumb: {
          label: "Template Manager",
          icon: <TemplatesIcon />,
        },
      },
      // Planner routes
      // grid-preview and planner both use the same page
      // can retire planner route in future if needed
      {
        path: "dashboard/grid-preview",
        element: (
          <LazyRoute>
            <PlannerPage />
          </LazyRoute>
        ),
        params: { title: "Grid Preview" },
        ncyBreadcrumb: {
          label: "Grid Preview",
          icon: <PlannerIcon />,
        },
      },
      {
        path: "dashboard/planner",
        element: <Navigate to="/dashboard/grid-preview" replace />,
      },
      {
        path: "dashboard/insights/analytics/audience",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/analytics/audience" />
        ),
      },
      {
        path: "dashboard/insights/analytics/audience/*",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/analytics/audience" />
        ),
      },
      {
        path: "dashboard/insights/analytics/competitors",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/social-listening/competitors" />
        ),
      },
      {
        path: "dashboard/insights/analytics/competitors/*",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/social-listening/competitors" />
        ),
      },
      {
        path: "dashboard/insights/audience",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/analytics/audience" />
        ),
      },
      {
        path: "dashboard/insights/audience/*",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/analytics/audience" />
        ),
      },
      {
        path: "dashboard/insights/ads",
        element: <RedirectWithParams to="/dashboard/insights-v2/ads" />,
      },
      {
        path: "dashboard/insights/labels",
        element: <RedirectWithParams to="/dashboard/insights-v2/labels" />,
      },
      {
        path: "dashboard/insights/report-builder/*",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/report-builder" />
        ),
      },
      // Social Listening is served exclusively through the in-repo
      // `@insights` app (mounted at `/dashboard/insights-v2/*`). Legacy
      // `/dashboard/social-listening/*` URLs are redirected so old links
      // (emails, deep links, bookmarks) land on the modern experience.
      {
        path: "dashboard/social-listening/sentiment/*",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/social-listening/sentiment" />
        ),
      },
      {
        path: "dashboard/social-listening/audience",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/social-listening/audience" />
        ),
      },
      {
        path: "dashboard/social-listening/audience/*",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/social-listening/audience" />
        ),
      },
      {
        path: "dashboard/social-listening/competitors/*",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/social-listening/competitors" />
        ),
      },
      {
        path: "dashboard/social-listening/keyword/*",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/social-listening/keyword" />
        ),
      },
      {
        path: "dashboard/social-listening/upgrade",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/social-listening/upgrade" />
        ),
      },
      {
        path: "dashboard/social-listening/no-access",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/social-listening/no-access" />
        ),
      },
      {
        path: "dashboard/social-listening/*",
        element: (
          <RedirectWithParams to="/dashboard/insights-v2/social-listening/summary" />
        ),
      },
      {
        path: "dashboard/insights-v2/*",
        element: (
          <LazyRoute>
            <InsightsApp />
          </LazyRoute>
        ),
        params: { title: "Insights" },
        ncyBreadcrumb: {
          icon: <InsightsIcon />,
          getLabel: (pathname) => {
            if (pathname.includes("/insights-v2/social-listening/audience")) {
              return "Insights";
            }
            if (pathname.includes("/insights-v2/social-listening")) {
              return "Social Listening";
            }
            return "Insights";
          },
          getSubLabel: (pathname) => {
            const base = "/dashboard/insights-v2/";
            if (pathname.includes(`${base}social-listening/sentiment`))
              return "Sentiment";
            if (pathname.includes(`${base}social-listening/competitors`))
              return "Competitors";
            if (pathname.includes(`${base}social-listening/keyword`))
              return "Keywords";
            if (pathname.includes(`${base}social-listening/audience`))
              return "Audience";
            if (pathname.includes(`${base}social-listening`)) return "Summary";
            if (pathname.includes(`${base}analytics/audience`))
              return "Audience";
            if (pathname.includes(`${base}labels`)) return "Label Performance";
            if (pathname.includes(`${base}ads`)) return "Paid Social";
            if (pathname.includes(`${base}report-builder`))
              return "Custom Reports";
            return "Brand Summary";
          },
        },
      },
      {
        path: "dashboard/insights/*",
        element: <RedirectWithParams to="/dashboard/insights-v2" />,
      },
      {
        path: "dashboard/cesdk",
        element: (
          <LazyRoute>
            <CesdkPage />
          </LazyRoute>
        ),
        params: { title: "Create assets" },
        ncyBreadcrumb: {
          label: "Create assets",
          icon: <SettingsIcon />,
          subLabel: "Create assets",
        },
      },
      {
        path: "dashboard/brand-kits",
        element: (
          <Navigate to="/dashboard/brand-kits/brand-description" replace />
        ),
        params: { title: "Brand Kits - Brand Description" },
        ncyBreadcrumb: {
          label: "Brand Kits | Brand Description",
          icon: <SettingsIcon />,
        },
      },
      {
        path: "dashboard/brand-kits/brand-description",
        element: (
          <LazyRoute>
            <BrandKitsPage />
          </LazyRoute>
        ),
        params: { title: "Brand Kits - Brand Description" },
        ncyBreadcrumb: {
          label: "Brand Kits | Brand Description",
          icon: <SettingsIcon />,
        },
      },
      {
        path: "dashboard/brand-kits/tone-of-voice",
        element: (
          <LazyRoute>
            <BrandKitsPage />
          </LazyRoute>
        ),
        params: { title: "Brand Kits - Tone of Voice" },
        ncyBreadcrumb: {
          label: "Brand Kits | Tone of Voice",
          icon: <SettingsIcon />,
        },
      },
      {
        path: "dashboard/brand-kits/instructions",
        element: (
          <LazyRoute>
            <BrandKitsPage />
          </LazyRoute>
        ),
        params: { title: "Brand Kits - Instructions" },
        ncyBreadcrumb: {
          label: "Brand Kits | Instructions",
          icon: <SettingsIcon />,
        },
      },
      {
        path: "dashboard/upload",
        element: <BulkUploadRedirect />,
        params: { title: "Bulk Upload" },
        ncyBreadcrumb: {
          // we refresh accounts after page is loaded
          label: "Upload",
          icon: <BulkUploadIcon />,
        },
      },
      {
        path: "dashboard/bulk",
        element: <BulkUploadRedirect />,
        params: { title: "Bulk Upload" },
        ncyBreadcrumb: {
          label: "Upload",
          icon: <BulkUploadIcon />,
        },
      },
      {
        path: "dashboard/style-guide",
        element: (
          <LazyRoute>
            <StyleGuidePage />
          </LazyRoute>
        ),
        params: { title: "Style Guide" },
      },
      {
        path: "admin/*",
        element: (
          <LazyRoute>
            <AdminApp />
          </LazyRoute>
        ),
        params: { title: "Admin" },
      },
      {
        path: "*",
        element: (
          <LazyRoute>
            <NotFoundPage />
          </LazyRoute>
        ),
        params: { title: "Page Not Found" },
      },
    ],
  },
];
const router = createBrowserRouter(routes);

export { router, routes };
